// Drawer «Agregar marca» / «Editar marca · {nombre}» (handoff §2): el
// nombre de la marca, con el copy que explica para qué sirve una marca.
// Crear es POST /organizations/me/brands; editar es PATCH
// /organizations/me/brands/:brandId, con «Guardar» deshabilitado hasta
// que el nombre cambie.
//
// Sin el campo «Código» (2 letras) del handoff: brands no tiene esa
// columna y esta ronda no migra, así que se oculta (regla «hide unusable
// actions»), no se simula. El copy de edición se recorta en consecuencia
// («solo su nombre»).

import { useId, useState } from "react";
import type { MyOrganizationBrand } from "@bitacorapro/api-types";
import { Drawer } from "../../../components/Drawer";
import { ApiError } from "../../../lib/api/error";
import { useCreateBrand, useRenameBrand } from "./queries";

export type MarcaDrawerMode = { kind: "new" } | { kind: "edit"; brand: MyOrganizationBrand };

export type MarcaDrawerProps = {
  mode: MarcaDrawerMode;
  /** «tiendas», «unidades»: el sustantivo de la receta, en minúscula. */
  assetPluralLower: string;
  onClose: () => void;
  /** Mensaje del toast que muestra la pantalla una vez cerrado el drawer. */
  onSaved: (message: string) => void;
};

export const BRAND_NAME_MAX = 120;

export function MarcaDrawer({ mode, assetPluralLower, onClose, onSaved }: MarcaDrawerProps) {
  const initialName = mode.kind === "edit" ? mode.brand.name : "";
  const [name, setName] = useState(initialName);
  const [error, setError] = useState<string | null>(null);
  const create = useCreateBrand();
  const rename = useRenameBrand();
  const nameId = useId();
  const errorId = useId();

  const pending = create.isPending || rename.isPending;
  const trimmed = name.trim();
  const changed = trimmed.length > 0 && trimmed !== initialName;
  const canSave = !pending && changed;

  async function save() {
    setError(null);
    try {
      if (mode.kind === "new") {
        await create.mutateAsync({ name: trimmed });
        onSaved(`Marca creada · ya puedes asignarle ${assetPluralLower}`);
      } else {
        await rename.mutateAsync({ brandId: mode.brand.id, body: { name: trimmed } });
        onSaved("Marca actualizada");
      }
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.code === "BRAND_NAME_TAKEN") {
        setError("Ya existe una marca con ese nombre.");
      } else if (err instanceof ApiError && err.code === "BRAND_NOT_FOUND") {
        setError("Esta marca ya no existe. Cierra el panel y vuelve a cargar la lista.");
      } else {
        setError(
          mode.kind === "new"
            ? "No pudimos crear la marca. Intenta de nuevo."
            : "No pudimos guardar los cambios. Intenta de nuevo."
        );
      }
    }
  }

  return (
    <Drawer
      title={mode.kind === "new" ? "Agregar marca" : `Editar marca · ${mode.brand.name}`}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="btn btn--ghost"
            title="Cerrar sin guardar cambios"
            disabled={pending}
            onClick={onClose}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn--primary"
            title={
              mode.kind === "new"
                ? "Crear la marca con este nombre"
                : "Guardar el nombre nuevo de esta marca"
            }
            disabled={!canSave}
            onClick={() => void save()}
          >
            {pending ? "Guardando…" : mode.kind === "new" ? "Crear marca" : "Guardar"}
          </button>
        </>
      }
    >
      <p className="ag-muted ag-mb16">
        {mode.kind === "new"
          ? `Una marca agrupa ${assetPluralLower} y permite invitar a un gerente de marca que solo ve las suyas.`
          : `Las ${assetPluralLower} y accesos de esta marca no cambian; solo su nombre.`}
      </p>
      {error && (
        <p id={errorId} className="auth-error" role="alert">
          {error}
        </p>
      )}
      <div className="field ag-field">
        <label className="field__label" htmlFor={nameId}>
          Nombre
        </label>
        <input
          id={nameId}
          className="input"
          value={name}
          onChange={(e) => {
            // El error del servidor habla del nombre anterior: se retira al
            // escribir otro, como en Datos legales.
            setName(e.target.value);
            setError(null);
          }}
          disabled={pending}
          maxLength={BRAND_NAME_MAX}
          aria-describedby={error ? errorId : undefined}
          aria-invalid={error ? true : undefined}
        />
      </div>
    </Drawer>
  );
}
